import { useState } from 'react';
import { AnalystButton } from '../components/Analyst/AnalystButton';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { Grain, PeriodKey, Source, Specialty, Targeting } from '../api/types.js';
import { ClientBreakdown } from '../components/ClientBreakdown/ClientBreakdown.js';
import {
  CHART_ROW_DATASET,
  ExportButton,
} from '../components/ExportButton/ExportButton.js';
import { LineChart } from '../components/LineChart/LineChart.js';
import { RegionSummary } from '../components/RegionSummary/RegionSummary.js';
import { SegmentedControl } from '../components/SegmentedControl/SegmentedControl.js';
import {
  EmptyState,
  ErrorBanner,
  NoSnapshotState,
  Skeleton,
  useReservedHeight,
} from '../components/States/States.js';
import { Header } from '../shell/Header.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import { CLIENT_DEFAULTS, comboKey, useChartFilters } from '../state/filters.js';
import {
  highlightFromParams,
  scopeFromParams,
  scopeToParams,
} from '../state/accountScope.js';
import {
  UI_REGIONS,
  accentFor,
  color,
  featuredPanel,
  fontSize,
  isUiRegion,
  radius,
} from '../theme/tokens.js';

/**
 * Region Detail (FR-036 – FR-041, FR-056, PRD §6.3, §6.4).
 *
 * Summary bar, a region-scoped chart, and the by-client breakdown.
 *
 * **The Nursing and Allied roster tables were removed on 2026-08-21** at the owner's request.
 * They listed the region's recruiters and leaders split by alignment `TYPE`, and were the page's
 * second and third tables. FR-041 is withdrawn with them. The API still returns `roster` on
 * `/api/regions/:code` — nothing renders it now.
 *
 * **Selecting a client scopes the chart to it; a clear control restores region scope**
 * (FR-040). The selection also arrives from ⌘K via the `?client=` parameter, which is what
 * lets a search result land with the row already highlighted and the chart already scoped
 * (FR-050) — the same state, reached two ways.
 *
 * Breakdown defaults are 30d / All (FR-038, PRD §6.4) — deliberately different from the
 * Overview table's 90d.
 */

const SOURCE_OPTIONS = [
  { value: 'all' as Source, label: 'All' },
  { value: 'internal' as Source, label: 'Internal' },
];
const TARGETING_OPTIONS = [
  { value: 'all' as Targeting, label: 'All' },
  { value: 'target' as Targeting, label: 'Target' },
];
const SPECIALTY_OPTIONS = [
  { value: 'all' as Specialty, label: 'All' },
  { value: 'nursing' as Specialty, label: 'Nursing' },
  { value: 'allied' as Specialty, label: 'Allied' },
];
const GRAIN_OPTIONS = [
  { value: 'daily' as Grain, label: 'Daily' },
  { value: 'weekly' as Grain, label: 'Weekly' },
  { value: 'monthly' as Grain, label: 'Monthly' },
  { value: 'quarterly' as Grain, label: 'Quarterly' },
];
/**
 * Shortest window first, so the track reads left to right as a widening lens.
 *
 * **`MTD` was added here on 2026-08-28** at the owner's request, having launched on Region
 * Performance the same day. It needed no server work: `PERIODS` has always included it, so
 * `parsePeriod` accepts it and `clients.ts` already stores a `byPeriod.MTD` block for every
 * client. Note it is the **only** option that is not a trailing window — MTD is the 1st of the
 * current month through as-of, so on the 1st it is a single day (PRD §8.4). ⚠ The breakdown DOES
 * draw trends — the arrow and its 30-day percentage on TOA, Jobs, Subs, Offers and Starts — and
 * `showsTrendArrows` does not govern them here as it does on the Overview table, so they stay on
 * MTD and YTD. (This note said "no trend arrows" until 2026-09-27; `clients.ts` has always stored
 * the trends and the cells have always drawn them.)
 */
const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string }> = [
  { value: 'MTD', label: 'MTD' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6M', label: '6M' },
  { value: '12M', label: '12M' },
  { value: '24M', label: '24M' },
  { value: 'YTD', label: 'YTD' },
];

export interface RegionDetailProps {
  onOpenSearch?: () => void;
}

/**
 * The address guard, and the reason this page is two components.
 *
 * A code that is not one of `UI_REGIONS` gets an explanation rather than a request. That covers
 * a typo, and since 2026-09-02 it covers **`/regions/SPA`** — the API still answers for SPA, so
 * without this the one route that names a region directly would keep serving the region every
 * other surface has stopped showing, reached from a stale link or a bookmark.
 *
 * It is a wrapper rather than an early return inside the page because the reads below are hooks:
 * the check has to happen before `useQuery` runs, and a hook cannot sit behind a condition.
 */
export function RegionDetail({ onOpenSearch }: RegionDetailProps) {
  const { regionCode = '' } = useParams();

  if (!isUiRegion(regionCode)) return <UnknownRegion regionCode={regionCode} />;

  return <RegionDetailView regionCode={regionCode} onOpenSearch={onOpenSearch} />;
}

/**
 * ⚠ Deliberately says nothing about SPA by name.
 *
 * The page cannot tell a retired code from a mistyped one, and naming the region would read as
 * a promise that it exists somewhere else in the product.
 */
function UnknownRegion({ regionCode }: { regionCode: string }) {
  // The snapshot header still has to be right: the address is wrong, the data behind the product
  // is not, and `no_data` here would say "No sync yet" over a perfectly healthy snapshot. It is
  // the one read this page makes, and stage 1 of the prefetch has almost always made it already.
  const sync = useQuery(queries.sync());

  return (
    <>
      <Header
        actions={<AnalystButton />}
        crumb="CYNET Health Commercial"
        title={regionCode || 'Region'}
        asOf={sync.data?.asOf ?? null}
        syncedAt={sync.data?.syncedAt ?? null}
        syncStatus={sync.data?.syncStatus ?? 'no_data'}
        staleness={sync.data?.staleness ?? null}
      />
      <div style={{ padding: '22px 30px 60px' }}>
        <EmptyState
          title={`${regionCode || 'That address'} is not a region on this dashboard`}
          // Listed off `UI_REGIONS` rather than written out, so the sentence cannot outlive the
          // set it describes — which is exactly how SPA came to be named in copy after it left.
          detail={`This dashboard reports ${UI_REGIONS.join(', ')}. Check the address, or pick a region from the overview.`}
          action={
            <Link
              to="/regions"
              style={{ color: color.nursing, fontSize: fontSize.bodyStrong, fontWeight: 600 }}
            >
              Back to all regions
            </Link>
          }
        />
      </div>
    </>
  );
}

function RegionDetailView({
  regionCode,
  onOpenSearch,
}: RegionDetailProps & { regionCode: string }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const chart = useChartFilters();
  const [clientPeriod, setClientPeriod] = useState<PeriodKey>(CLIENT_DEFAULTS.period);
  const [clientTargeting, setClientTargeting] = useState<Targeting>(CLIENT_DEFAULTS.targeting);

  /*
   * The URL is the source of truth for both, and they are two different things (FR-076).
   *
   * `scope` is what the CHART is drawing — an MSP, a parent client or a child client.
   * `selectedClientKey` is which BREAKDOWN row is highlighted, and it is always a parent,
   * because that table is parent-level. They coincide for a parent-client selection and diverge
   * for the other two: a child highlights its parent, and an MSP highlights nothing.
   *
   * ⚠ Deriving the highlight from `scope` instead would highlight a facility key against a
   * table that has no such row — a selection that silently does nothing — or, worse, a parent
   * row whose figures are not what the chart above it is drawing.
   */
  const scope = scopeFromParams(searchParams);
  const selectedClientKey = highlightFromParams(searchParams);

  // Through the client store. The prefetch holds every region's detail, breakdown and
  // unscoped chart, so arriving from a table row is usually instant; a client-scoped chart is
  // fetched on selection, since there are hundreds of clients per region.
  const detail = useQuery(queries.regionDetail(regionCode));
  const clients = useQuery(
    queries.regionClients(regionCode, { period: clientPeriod, targeting: clientTargeting }),
  );
  const timeseries = useQuery(
    queries.regionTimeseries(regionCode, {
      ...chart.filters,
      scopeType: scope?.scopeType ?? null,
      scopeKey: scope?.scopeKey ?? null,
    }),
  );

  const envelope = detail.data;
  const data = envelope?.data ?? null;
  const unattributedBooks = envelope?.dataQuality?.unattributedBooks ?? null;

  const selectedClient = clients.data?.data?.rows.find(
    (r) => r.clientKey === selectedClientKey,
  );
  const scopeName = timeseries.data?.data?.scopeName ?? null;

  /**
   * Hold the breakdown's height while its own Period / Targeting controls refetch it.
   *
   * Those two controls live in that table's header, and a region with three hundred clients
   * collapsing to a 280px skeleton scrolls the page out from under the reader mid-click. The
   * height is the client count's, so it is measured rather than guessed.
   */
  const breakdownHeight = useReservedHeight(clients.loading && !clients.data);

  /**
   * Clicking a breakdown row selects that PARENT client at both levels.
   *
   * ⚠ Routed through `scopeToParams` rather than setting `?client=` directly, so it clears any
   * `scopeType`/`scopeKey` left by an MSP or child selection. Without that, picking a row while
   * a facility was charted would highlight the row and leave the chart on the facility — the
   * table and the plot answering two different questions on one screen.
   */
  function selectClient(clientKey: string) {
    const isCurrent = scope?.scopeType === 'client' && scope.scopeKey === clientKey;
    setSearchParams(
      scopeToParams(searchParams, {
        scope: isCurrent ? null : { scopeType: 'client', scopeKey: clientKey },
      }),
      { replace: true },
    );
  }

  /** Back to the whole region, from any of the three levels. */
  function clearScope() {
    setSearchParams(scopeToParams(searchParams, { scope: null }), { replace: true });
  }

  const summaryMetrics =
    data?.summary?.[`${clientPeriod}|${comboKey(chart.filters)}`] ??
    data?.summary?.[`${clientPeriod}|all|all|all`] ??
    data?.summary?.['3M|all|all|all'] ??
    {};

  return (
    <>
      <Header
        actions={<AnalystButton />}
        crumb="CYNET Health Commercial"
        title={data ? `${data.regionCode} · ${data.regionName}` : regionCode}
        asOf={envelope?.asOf ?? null}
        syncedAt={envelope?.syncedAt ?? null}
        syncStatus={envelope?.syncStatus ?? 'no_data'}
        staleness={envelope?.staleness ?? null}
        unattributedBooks={unattributedBooks}
        onOpenSearch={onOpenSearch}
      />

      <div style={{ padding: '22px 30px 60px' }}>
        <Link
          to="/regions"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: fontSize.bodyStrong,
            fontWeight: 500,
            color: color.inkMuted,
            textDecoration: 'none',
            marginBottom: 14,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          All regions
        </Link>

        {detail.error ? (
          <ErrorBanner
            message={`Could not load ${regionCode}. ${detail.error.message}`}
            onRetry={detail.reload}
          />
        ) : null}

        {envelope?.syncStatus === 'no_data' ? (
          <NoSnapshotState />
        ) : detail.loading && !data ? (
          <Skeleton height={180} />
        ) : data?.isEmpty ? (
          // A known region with nothing in this snapshot: an empty state, not a page of zeros
          // (FR-056, PRD §6.5).
          <EmptyState
            title={`No data for ${regionCode}`}
            detail="This region has no rows in the current snapshot. That is different from every figure being zero — nothing has been recorded against it."
            action={
              <Link
                to="/regions"
                style={{ color: color.nursing, fontSize: fontSize.bodyStrong, fontWeight: 600 }}
              >
                Back to all regions
              </Link>
            }
          />
        ) : data ? (
          <>
            <RegionSummary
              regionCode={data.regionCode}
              regionName={data.regionName}
              isSpecial={data.isSpecial}
              leaders={data.leaders}
              states={data.states}
              metrics={summaryMetrics}
              unattributedBooks={unattributedBooks}
            />

            {/* The featured edge, as on Overview (owner request 2026-08-30): the chart card wears
                it on every page that has one, or the treatment would read as a property of the
                Overview rather than of the chart. */}
            <section
              aria-label="Region chart"
              style={{
                ...featuredPanel,
                borderRadius: radius['6xl'],
                padding: '18px 20px',
                marginBottom: 18,
              }}
            >
              {/*
                One row: series chips left, filters right (blueprint § region chart). They were
                previously two stacked rows above the title, which put the controls before the
                thing they control and left a band of dead space when no client was selected.
              */}
              <LineChart
                data={timeseries.data?.data ?? null}
                /* The bucket windows in the hover bubble end at the sync, not at the as-of
                   day's calendar edge, on the newest bucket (FR-087). */
                syncedAt={timeseries.data?.syncedAt ?? null}
                loading={timeseries.loading}
                titleColor={accentFor(data.regionCode)}
                /*
                 * Names what is actually charted, at whichever of the three levels it is.
                 *
                 * The fallback chain is ordered by how quickly each source can answer, not by
                 * preference: `scopeName` is authoritative but arrives with the chart response,
                 * the breakdown row is already in hand from a cached list, and the key is the
                 * last resort. Without the middle step the title flickers from the region name
                 * to the client's on every parent selection — the one case that is instant.
                 */
                title={scopeTitle(data.regionName, scopeName, selectedClient?.clientName, scope)}
                /* Same two glyphs as the Overview's chart (owner request 2026-09-09): the same
                   component on both region pages must offer the same readings, or the affordance
                   reads as a property of the Overview rather than of the chart. */
                tableView
                /*
                  The jobs export (FR-088). This page is always exactly one region, so the button
                  is never in the Overview's disabled state.
                  ⚠ **It exports the REGION, not the account the chart is scoped to.** The stored
                  rows are keyed by region (`job_export_rows`), and the button says which region
                  it is exporting on its face and in its accessible name — so a reader scoped to
                  one client reads "Export R3 jobs" and is not told the sheet will be that
                  client's. Narrowing the export to the scope would need the rows keyed by
                  account as well, which is a much larger collection for a second question.
                */
                actions={
                  <ExportButton
                    regionCode={data.regionCode}
                    window={{ grain: chart.filters.grain }}
                  />
                }
                /*
                  The matrix's own export column (FR-093, owner request 2026-09-14) — one
                  control per row, each writing that row's dataset alone.

                  ⚠ **It does NOT replace `actions` above.** That button is the card's: the
                  region, both tabs, reachable from the plot where there are no rows to press.
                  These two are the JOBS and SUBS rows saying what they are made of, and they
                  exist only while the matrix is on screen.

                  ⚠ **Same window as the card's button — the chart's GRAIN, not the table's
                  period.** The rows being exported are the twelve buckets this chart is
                  drawing; cutting them by the Region Performance period below would export a
                  window nothing on this card names.
                */
                onExportRow={(key) => {
                  const dataset = CHART_ROW_DATASET[key];
                  // ⚠ `null`, never a disabled control: TOA and OFFERS have no export, and a
                  // dead button in a four-row table reads as a fault rather than an absence.
                  if (!dataset) return null;
                  return (
                    <ExportButton
                      regionCode={data.regionCode}
                      window={{ grain: chart.filters.grain }}
                      dataset={dataset}
                    />
                  );
                }}

                titleAdornment={
                  scope ? (
                    <button
                      type="button"
                      onClick={clearScope}
                      style={{
                        fontSize: fontSize.caption,
                        fontWeight: 600,
                        color: color.inkMuted,
                        background: color.surfaceChip,
                        border: `1px solid ${color.borderChip}`,
                        borderRadius: radius.pill,
                        padding: '2px 9px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      ✕ clear {scope.scopeType === 'client' ? 'client' : SCOPE_LABEL[scope.scopeType]}
                    </button>
                  ) : null
                }
                controls={
                  <>
                    <SegmentedControl
                      label="Chart source"
                      options={SOURCE_OPTIONS}
                      value={chart.filters.source}
                      onChange={(v) => chart.set('source', v)}
                    />
                    <SegmentedControl
                      label="Chart targeting"
                      options={TARGETING_OPTIONS}
                      value={chart.filters.targeting}
                      onChange={(v) => chart.set('targeting', v)}
                    />
                    <SegmentedControl
                      label="Chart specialty"
                      options={SPECIALTY_OPTIONS}
                      value={chart.filters.specialty}
                      onChange={(v) => chart.set('specialty', v)}
                    />
                    <SegmentedControl
                      label="Chart grain"
                      options={GRAIN_OPTIONS}
                      value={chart.filters.grain}
                      onChange={(v) => chart.set('grain', v)}
                    />
                  </>
                }
              />
            </section>

            <section
              aria-label="By client"
              ref={breakdownHeight.ref}
              style={{ marginBottom: 22, ...breakdownHeight.style }}
            >
              {clients.error ? (
                <ErrorBanner
                  message="Could not load the client breakdown."
                  onRetry={clients.reload}
                />
              ) : clients.loading && !clients.data ? (
                <Skeleton height={280} />
              ) : (
                <ClientBreakdown
                  rows={clients.data?.data?.rows ?? []}
                  selectedClientKey={selectedClientKey}
                  onSelectClient={selectClient}
                  /*
                    The per-account export (FR-091) — the same two tabs and the same columns as
                    the region export above, holding one account's rows.

                    ⚠ **Cut by THIS card's period, not the chart's grain.** Two controls on two
                    cards, and each export follows the one it was launched from: the chart's
                    button takes `chart.filters.grain`, this one takes `clientPeriod`, and the
                    route refuses both at once rather than inventing a precedence. A reader who
                    narrowed the breakdown to `30d` and got twelve months of rows would have no
                    way to tell which control had been ignored.

                    ⚠ It exports the **row**, never the chart's scope. The two diverge constantly
                    — the chart may be scoped to an MSP or a facility, or to nothing — and the
                    control that sits in a row has to mean that row.
                  */
                  onExportClient={(row) => (
                    <ExportButton
                      regionCode={data.regionCode}
                      window={{ period: clientPeriod }}
                      client={{ clientKey: row.clientKey, clientName: row.clientName }}
                    />
                  )}
                  // Inside the card, above the table — one bordered surface, not a floating
                  // title over a separate one. Period precedes targeting, as the blueprint has it.
                  header={
                    <>
                      <div
                        style={{
                          fontSize: fontSize.control,
                          fontWeight: 600,
                          color: color.ink,
                        }}
                      >
                        Breakdown{' '}
                        <span style={{ color: color.inkFaint, fontWeight: 500 }}>
                          · By Client
                        </span>
                      </div>
                      <div
                        style={{
                          marginLeft: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'wrap',
                        }}
                      >
                        <SegmentedControl
                          label="Client period"
                          options={PERIOD_OPTIONS}
                          value={clientPeriod}
                          onChange={setClientPeriod}
                        />
                        <SegmentedControl
                          label="Client targeting"
                          options={TARGETING_OPTIONS}
                          value={clientTargeting}
                          onChange={setClientTargeting}
                        />
                        {/*
                          Every account in this region, two tabs each (FR-092).

                          ⚠ **It sits beside the two controls that govern it**, which is the same
                          rule the chart's export follows: Period cuts the rows and Targeting
                          decides which accounts are listed at all — and therefore how many tabs
                          the file has. A control has to be visible next to what moves it.

                          ⚠ It is the card's **toolbar** button, next to the row-level ones in the
                          action column: one covers every account, each of those covers its own
                          row. Same relationship as Region Performance's toolbar button to its
                          row controls (FR-090).
                        */}
                        <ExportButton
                          regionCode={data.regionCode}
                          window={{ period: clientPeriod }}
                          allClients={{ targeting: clientTargeting }}
                        />
                      </div>
                    </>
                  }
                />
              )}
            </section>
          </>
        ) : null}
      </div>
    </>
  );
}

/**
 * How the clear chip names the level it is clearing.
 *
 * ⚠ It says what is being *removed*, not what a row is — so it is lower case and singular and
 * deliberately NOT `NODE_TYPE_LABEL`, which titles a table column ("Parent client"). "✕ clear
 * MSP" beside a chart of an MSP reads; "✕ clear Parent client" does not.
 */
const SCOPE_LABEL: Record<'msp' | 'child', string> = {
  msp: 'MSP',
  child: 'facility',
};

/**
 * The chart's title, and its accessible name.
 *
 * Falls back through three sources because they become available at three different times —
 * see the call site. The last one is the raw key, which is lower-cased and space-collapsed: it
 * is not pretty, and it is still better than a title that says only the region while the plot
 * is drawing one facility inside it.
 */
function scopeTitle(
  regionName: string,
  scopeName: string | null,
  clientName: string | undefined,
  scope: { scopeType: string; scopeKey: string } | null,
): string {
  if (!scope) return regionName;
  const name = scopeName ?? clientName ?? scope.scopeKey;
  return `${regionName} · ${name}`;
}
